<template>
  <div class="edit-textbook">
    <TextbookPageNav
      v-if="$route.query.barcode"
      :barcode="$route.query.barcode"
      active-tab="textbook"
    />

    <VeeForm
      ref="formRef"
      :validation-schema="schema"
      @submit="onSubmit"
    >
      <APageHead :title="$t('txt.10')" :sub="$t('txt.11')">
        <template #actions>
          <template v-if="$route.query.barcode">
            <APill :kind="statusPillKind" size="lg">{{ statusLabel }}</APill>
            <span class="edit-textbook__version">
              <i class="material-icons-round">layers</i>
              배포 버전 v{{ textbooksData.major_version }}.{{ textbooksData.minor_version }}
            </span>
            <button
              type="button"
              class="edit-textbook__history-btn"
              title="버전 이력 보기"
              @click="openVersionHistoryModal"
            >
              <i class="material-icons-round">history</i>
            </button>

            <!-- 승인 게이트(학습단계·도서분야·테마)가 비면 서버가 400 을 냅니다(가이드 §2).
                 눌러서 400 을 맞기 전에 무엇이 비었는지 여기서 먼저 보여줍니다. -->
            <APill
              v-if="currentStatus === 'request' && missingApprovalFacets.length"
              kind="warn"
              dot
              v-tooltip
              title="분류값을 지정하고 저장해야 승인할 수 있습니다."
            >분류 미입력: {{ missingApprovalFacets.join(", ") }}</APill>

            <!-- 승인·반려·해제 모두 관리자 전용이 됐습니다(가이드 §3).
                 저자에게는 전이 규칙이 있지만 관리자에게는 없어, 승인된 교재를 다시
                 내리는 것도 여기서만 가능합니다. 선택지는 statusActions 가 상태별로 만듭니다. -->
            <select
              v-if="statusActions.length"
              class="p-input edit-textbook__status-select"
              :value="''"
              @change="onStatusChange($event.target.value); $event.target.value = ''"
            >
              <option value="" disabled>상태 변경</option>
              <option v-for="a in statusActions" :key="a.value" :value="a.value">{{ a.label }}</option>
            </select>

            <ABtn kind="ghost" icon="north" @click="minorVersionUp">Minor 버전업</ABtn>
            <ABtn kind="danger" icon="north" @click="majorVersionUp">Major 버전업</ABtn>
          </template>

          <ABtn kind="yellow" icon="save" type="submit">{{ $t("btn.04") }}</ABtn>

          <ABtn
            v-if="$route.query.barcode"
            kind="danger-fill"
            icon="delete"
            @click="deleteTextbook"
          >{{ $t("btn.11") }}</ABtn>
        </template>
      </APageHead>

      <div class="edit-textbook__layout">
        <ACard class="edit-textbook__cover">
          <input id="upload-image" type="file" hidden accept=".jpg,.jpeg" @change="getFileName($event.target.files)" />
          <label for="upload-image" class="edit-textbook__cover-frame">
            <img
              id="preview"
              :src="selectedCoverPreview || previewImage || require('@/assets/img/no_image.png')"
              alt="교재 표지"
              class="edit-textbook__cover-img"
            />
            <span class="edit-textbook__cover-overlay">
              <i class="material-icons-round">photo_camera</i>
              <span>표지 이미지 변경</span>
            </span>
          </label>
          <p class="edit-textbook__cover-hint">JPG / JPEG 형식만 업로드 가능합니다.</p>

          <!-- 저장은 끝났고 표지 업로드만 실패한 상태입니다. [저장]을 다시 누르게 하지 않고
               업로드만 다시 합니다(docs/교재_등록_표지업로드.md §3). -->
          <div v-if="coverUploadError" class="edit-textbook__cover-error">
            <p class="edit-textbook__cover-error-title">
              <i class="material-icons-round">error_outline</i>
              교재는 저장됐고 표지만 올라가지 않았습니다.
            </p>
            <p class="edit-textbook__cover-error-reason">{{ coverUploadError }}</p>
            <p class="edit-textbook__cover-error-hint">다른 파일로 바꾸려면 위 표지를 눌러 고른 뒤 누르세요.</p>
            <ABtn kind="yellow" size="sm" icon="upload" block :loading="coverUploading" @click="retryCoverUpload">
              표지 다시 올리기
            </ABtn>
          </div>
        </ACard>

        <ACard :title="$t('label.35')">
          <!-- 이 화면의 신규 등록은 editor_idx 없이 교재를 만듭니다. 편집 권한이 textbook_editor
               연결로 판정되므로(가이드 §4) 그렇게 만든 교재는 저자가 손댈 수 없습니다.
               저자 연결은 [저자 상세 > 저작물 관리]에서만 걸 수 있어 여기서는 안내만 합니다. -->
          <div v-if="!$route.query.barcode" class="edit-textbook__facet-gate">
            <i class="material-icons-round">person_off</i>
            <span>
              여기서 등록한 교재에는 <b>저자 연결이 생기지 않습니다.</b>
              저자가 직접 편집하려면 등록 후 <b>[저자 상세 &gt; 저작물 관리]</b>에서 저작물로 추가해주세요.
            </span>
          </div>

          <div class="p-form-grid p-form-grid--4 edit-textbook__form-row">
            <Field v-slot="{ field, errors }" name="barcode">
              <div class="p-field">
                <label class="p-field__label" for="barcode">{{ $t('label.28') }} <span class="p-field__req">*</span></label>
                <input
                  id="barcode"
                  v-bind="field"
                  class="p-input"
                  :class="{ 'is-invalid': errors.length }"
                  :disabled="!!$route.query.barcode"
                  type="text"
                  :placeholder="$t('label.28')"
                />
                <ErrorMessage name="barcode" class="p-field__error" />
              </div>
            </Field>

            <Field v-slot="{ field, errors }" name="title">
              <div class="p-field">
                <label class="p-field__label" for="title">{{ $t('label.14') }} <span class="p-field__req">*</span></label>
                <input
                  id="title"
                  v-bind="field"
                  class="p-input"
                  :class="{ 'is-invalid': errors.length }"
                  type="text"
                  :placeholder="$t('label.14')"
                />
                <ErrorMessage name="title" class="p-field__error" />
              </div>
            </Field>

            <Field v-slot="{ field, errors }" name="publisher">
              <div class="p-field">
                <label class="p-field__label" for="publisher">{{ $t('label.29') }} <span class="p-field__req">*</span></label>
                <input
                  id="publisher"
                  v-bind="field"
                  class="p-input"
                  :class="{ 'is-invalid': errors.length }"
                  type="text"
                  :placeholder="$t('label.29')"
                />
                <ErrorMessage name="publisher" class="p-field__error" />
              </div>
            </Field>

            <!-- 테마는 승인 게이트 대상입니다(가이드 §2) — 비우면 승인이 400 입니다. -->
            <Field v-slot="{ field }" name="theme">
              <div class="p-field">
                <label class="p-field__label">{{ $t('label.37') }} <span class="p-field__req">*</span></label>
                <select
                  class="p-input"
                  :value="field.value"
                  @change="field.onInput($event.target.value); textbooksData.theme = $event.target.value"
                  @blur="field.onBlur"
                >
                  <option value="">Select theme</option>
                  <option v-for="opt in themeSelectOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                </select>
                <ErrorMessage name="theme" class="p-field__error" />
              </div>
            </Field>
          </div>

          <div class="p-form-grid p-form-grid--4 edit-textbook__form-row">
            <div class="p-field">
              <MultipleSelect
                id="fonts-select"
                v-model="selectedFonts"
                label="Fonts"
                :options="fontOptions"
                placeholder="Select fonts"
                :allow-search="true"
                :multiple="true"
                display-mode="count"
              />
            </div>
          </div>

          <!-- 분류: 학습단계·도서분야·학습언어·대상언어·시리즈. 옵션은 전부 서버에서 동적 조달합니다(idx·표시명 하드코딩 금지).
               2026-08-28 정책 변경으로 저자 화면에서 분류 입력이 빠졌습니다 — 이 4개를 채울 수 있는 곳은 여기뿐입니다. -->
          <div class="edit-textbook__facets">
            <div v-if="facetOptionsFailed" class="edit-textbook__facet-error">
              <i class="material-icons-round">error_outline</i>
              <span>분류 옵션을 불러오지 못했습니다. 학습단계를 선택하려면 다시 시도해주세요.</span>
              <ABtn size="sm" kind="ghost" icon="refresh" @click="getFilterOptions">다시 시도</ABtn>
            </div>

            <!-- 이미 승인된 교재에도 뜰 수 있어(정책 변경 전 승인분) 문구는 현재 상태를 단정하지 않습니다. -->
            <div v-if="$route.query.barcode && missingApprovalFacets.length" class="edit-textbook__facet-gate">
              <i class="material-icons-round">report_problem</i>
              <span>
                <b>{{ missingApprovalFacets.join(", ") }}</b> 이(가) 비어 있습니다.
                승인하려면 여기서 지정한 뒤 [저장]을 눌러주세요.
              </span>
            </div>

            <Field v-slot="{ field }" name="grade">
              <div class="p-field">
                <label class="p-field__label">학습단계 <span class="p-field__req">*</span></label>
                <div class="edit-textbook__facet-checks">
                  <label v-for="opt in gradeOptions" :key="opt.code" class="p-check">
                    <input
                      type="radio"
                      name="grade_radio"
                      :value="opt.code"
                      :checked="field.value === opt.code"
                      @change="field.onInput(opt.code); textbooksData.grade = opt.code"
                    />
                    <span>{{ opt.name }}</span>
                  </label>
                </div>
                <ErrorMessage name="grade" class="p-field__error" />
              </div>
            </Field>

            <div class="p-field">
              <div class="edit-textbook__facet-head">
                <label class="p-field__label">도서분야 <span class="edit-textbook__hint">(선택·복수 가능)</span></label>
                <ABtn size="sm" kind="ghost" icon="settings" @click="openFacetModal('field')">관리</ABtn>
              </div>
              <div class="edit-textbook__facet-checks">
                <label v-for="opt in fieldOptions" :key="opt.idx" class="p-check">
                  <input type="checkbox" :value="opt.idx" v-model="selectedFieldIdxs" />
                  <span>{{ opt.name }}</span>
                </label>
                <span v-if="!fieldOptions.length && !facetOptionsFailed" class="edit-textbook__facet-empty">등록된 분야가 없습니다. [관리]에서 추가하세요.</span>
              </div>
            </div>

            <!-- 학습언어와 대상언어는 반대 개념입니다(가이드 §1). 한국어 교재라면
                 학습언어 = 한국어(가르치는 언어), 대상언어 = 영어(영어권 학습자 대상).
                 라벨만으로는 매번 헷갈리는 자리라 두 필드를 붙여 두고 예시를 함께 답니다. -->
            <div class="edit-textbook__lang-pair">
              <div class="p-field">
                <div class="edit-textbook__facet-head">
                  <label class="p-field__label" for="learning_language">
                    학습언어 <span class="edit-textbook__hint">(이 교재가 <b>가르치는</b> 언어 · 1개)</span>
                  </label>
                </div>
                <select
                  id="learning_language"
                  v-model="textbooksData.learning_language"
                  class="p-input"
                >
                  <option value="">선택 안 함</option>
                  <option v-for="opt in learningLanguageSelectOptions" :key="opt.code" :value="opt.code">{{ opt.label }}</option>
                </select>
                <span v-if="learningLanguageFailed" class="edit-textbook__facet-empty">
                  언어 목록을 불러오지 못했습니다. 저장하면 기존 값이 그대로 유지됩니다.
                </span>
              </div>

              <div class="p-field">
                <div class="edit-textbook__facet-head">
                  <label class="p-field__label">대상언어 <span class="edit-textbook__hint">(이 교재를 <b>배우는 학습자의 모국어</b> · 선택·복수 가능)</span></label>
                  <ABtn size="sm" kind="ghost" icon="settings" @click="openFacetModal('language')">관리</ABtn>
                </div>
                <div class="edit-textbook__facet-checks">
                  <label v-for="opt in targetLanguageOptions" :key="opt.idx" class="p-check">
                    <input type="checkbox" :value="opt.idx" v-model="selectedLanguageIdxs" />
                    <span>{{ opt.name }}</span>
                  </label>
                  <span v-if="!targetLanguageOptions.length && !facetOptionsFailed" class="edit-textbook__facet-empty">등록된 언어가 없습니다. [관리]에서 추가하세요.</span>
                </div>
              </div>
            </div>

            <p class="edit-textbook__series-hint">
              <i class="material-icons-round">info</i>
              <span>예) 영어권 학습자를 위한 한국어 교재 → 학습언어 <b>한국어</b>, 대상언어 <b>영어</b>.</span>
            </p>

            <!-- 시리즈 / 권차 : 시리즈는 선택 항목이며, 미지정(null)이 정상 상태입니다. -->
            <div class="edit-textbook__series-row">
            <div class="p-field edit-textbook__series-select">
              <label class="p-field__label" for="series_idx">
                시리즈 <span class="edit-textbook__hint">(선택)</span>
              </label>
              <div class="edit-textbook__series-control">
                <select
                  id="series_idx"
                  class="p-input"
                  :value="textbooksData.series_idx"
                  @change="onSeriesChange($event.target.value)"
                >
                  <option value="">선택 안 함</option>
                  <option v-for="opt in seriesOptions" :key="opt.idx" :value="opt.idx">{{ opt.name }}</option>
                </select>
                <ABtn kind="ghost" icon="settings" @click="showSeriesModal = true">관리</ABtn>
              </div>
            </div>

            <div class="p-field edit-textbook__series-volume">
              <label class="p-field__label" for="series_volume">권차</label>
              <div class="edit-textbook__volume-control">
                <input
                  id="series_volume"
                  v-model="textbooksData.series_volume"
                  class="p-input"
                  type="number"
                  min="1"
                  :disabled="!textbooksData.series_idx"
                  placeholder="1"
                />
                <span class="edit-textbook__volume-unit">권</span>
              </div>
            </div>

            <p class="edit-textbook__series-hint">
              <i class="material-icons-round">info</i>
              <span v-if="!textbooksData.series_idx">시리즈를 해제하면 권차도 함께 지워집니다.</span>
              <span v-else-if="!textbooksData.series_volume">권차를 입력하지 않으면 시리즈 목록 맨 뒤에 표시됩니다.</span>
              <span v-else>시리즈 안에서의 순번입니다. 숫자만 입력하세요.</span>
            </p>
            </div>
          </div>

          <div class="p-field edit-textbook__editor-field">
            <label class="p-field__label">
              Description
              <span class="edit-textbook__hint">(선택)</span>
            </label>
            <div class="p-editor-wrap">
              <QuillEditor v-model="textbooksData.description" :config="editorConfig" />
            </div>
          </div>
        </ACard>

        <ACard :title="$t('label.34')" class="edit-textbook__pricing">
          <div v-show="!textbooksData.is_free_copy" class="edit-textbook__price-row">
            <div class="p-field">
              <label class="p-field__label">{{ $t('label.40') }} <span class="p-field__req">*</span></label>
              <Field v-slot="{ field }" name="product_type">
                <select
                  class="p-input"
                  :value="field.value"
                  @change="field.onInput($event.target.value)"
                  @blur="field.onBlur"
                  v-tooltip="'제품의 유형에 따라 판매 가격이 달라집니다.'"
                >
                  <option value="">Select a product type</option>
                  <option v-for="opt in productTypeOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                </select>
                <ErrorMessage name="product_type" class="p-field__error" />
              </Field>
            </div>
            <div class="edit-textbook__price-actions">
              <ABtn kind="ghost" icon="visibility" @click="openModal">가격표 보기</ABtn>
            </div>
          </div>

          <label class="p-check edit-textbook__free-toggle">
            <input
              id="is_free_copy"
              v-model="textbooksData.is_free_copy"
              type="checkbox"
            />
            <span class="edit-textbook__free-label">
              <strong>비매품 (사은품)</strong>
              <span class="edit-textbook__hint">비매품 교재는 출판 승인 요청이 불필요합니다.</span>
            </span>
          </label>

          <!-- 인세 방식은 저자 저작물 화면이 어떤 값을 받는지까지 결정합니다 — 반대 필드를 보내면 400 입니다(가이드 §9.5). -->
          <div class="p-field edit-textbook__royalty-type">
            <label class="p-field__label">인세 방식 <span class="p-field__req">*</span></label>
            <div class="edit-textbook__royalty-type-row">
              <label v-for="opt in royaltyTypeOptions" :key="opt.value" class="p-check">
                <input
                  v-model="textbooksData.royalty_type"
                  type="radio"
                  :value="opt.value"
                  @change="royaltyTypeTouched = true"
                />
                <span>{{ opt.label }}</span>
              </label>
            </div>
            <p class="edit-textbook__series-hint">
              <i class="material-icons-round">info</i>
              <span>이 도서의 저자 인세는 선택한 방식(인세율 % / 1copy당 금액)으로만 등록할 수 있습니다.</span>
            </p>
          </div>

          <div v-if="!isAmountRoyalty" class="p-field edit-textbook__royalty-cap">
            <label class="p-field__label" for="max_royalty_rate">
              최대 인세율
              <span class="edit-textbook__hint">(이 도서에 걸 수 있는 저자 인세율의 합계 상한, 0~35%)</span>
            </label>
            <div class="edit-textbook__royalty-cap-row">
              <input
                id="max_royalty_rate"
                v-model.number="textbooksData.max_royalty_rate"
                type="number"
                class="p-input"
                min="0"
                max="35"
                step="0.01"
              />
              <span class="edit-textbook__volume-unit">%</span>
            </div>
            <p class="edit-textbook__series-hint">
              <i class="material-icons-round">info</i>
              <span>이미 등록된 저자 인세율 합계보다 낮게는 내릴 수 없습니다.</span>
            </p>
          </div>

          <div v-else class="p-field edit-textbook__royalty-cap">
            <label class="p-field__label" for="max_royalty_amount">
              최대 인세금액
              <span class="edit-textbook__hint">(1copy당 저자 인세금액의 합계 상한)</span>
            </label>
            <div class="edit-textbook__royalty-amount-row">
              <input
                id="max_royalty_amount"
                v-model.number="textbooksData.max_royalty_amount"
                type="number"
                class="p-input"
                min="0"
                step="0.01"
              />
              <!--
                통화는 currency 마스터의 코드만 허용됩니다(그 외 400) — 직접 입력을 두지 않는 이유입니다.
                2026-09-11 부터 서버가 처음 보는 결제 통화를 자동 등록하므로 이 목록은 스스로 늘어납니다.
                KRW·USD 로 좁히지 않는 이유: 이건 판매 가격이 아니라 저자에게 지급할 인세 상한이라
                에디터 선호 화폐와 같은 기준으로 고를 수 있어야 합니다.
              -->
              <select
                id="max_royalty_currency"
                v-model="textbooksData.max_royalty_currency"
                class="p-input edit-textbook__royalty-currency"
              >
                <option value="">통화</option>
                <option v-for="c in currencyOptions" :key="c.code" :value="c.code">{{ c.code }}</option>
              </select>
            </div>
            <p class="edit-textbook__series-hint">
              <i class="material-icons-round">info</i>
              <span>이미 등록된 저자 인세금액 합계보다 낮게는 내릴 수 없습니다.</span>
            </p>
          </div>

          <div class="edit-textbook__grade-section">
            <label class="p-field__label edit-textbook__grade-heading">
              판매 하려는 가격 등급
              <button
                type="button"
                class="edit-textbook__info-btn"
                title="등급별 포함 기능 안내"
                @click="showPricingGradeInfo = true"
              >
                <i class="material-icons-round">info</i>
              </button>
            </label>
            <div class="edit-textbook__grade-row">
              <label
                v-for="option in pricingGradeOptions"
                :key="option.value"
                class="p-check"
                :class="{ 'is-disabled': option.value === 'basic' }"
              >
                <input
                  type="checkbox"
                  :value="option.value"
                  v-model="selectedPricingGrades"
                  :disabled="option.value === 'basic'"
                />
                <span>{{ option.label }}</span>
              </label>
            </div>
          </div>

          <div class="edit-textbook__tags">
            <MultipleSelect
              id="tags-select"
              v-model="selectedTags"
              label="Tags"
              :options="tagOptions"
              placeholder="Select tags"
              :allow-search="true"
              :multiple="true"
            />
            <div class="edit-textbook__tag-input">
              <input
                v-model="newTagInput"
                type="text"
                class="p-input"
                placeholder="새 태그 직접 입력 후 Enter"
                @keydown.enter.prevent="handleTagEnter"
              />
              <ABtn kind="ghost" icon="add" @click="addCustomTag">추가</ABtn>
            </div>
          </div>
        </ACard>
      </div>
    </VeeForm>

    <!-- 시리즈 관리 모달 -->
    <TextbookSeriesModal
      v-model="showSeriesModal"
      :selected-idx="textbooksData.series_idx"
      @changed="onSeriesChanged"
    />

    <!-- 도서분야·대상언어 마스터 관리 모달 (공용) -->
    <TextbookFacetModal
      v-model="showFacetModal"
      :facet="activeFacet"
      @changed="onFacetMasterChanged"
    />

    <!-- 가격표 모달 -->
    <AModal v-model="isModalVisible" title="가격표" size="xl">
      <!-- 가격을 수정하는 관리자 API 가 없습니다. 편집 버튼을 만들지 않는 이유를 화면에 밝혀 둡니다. -->
      <template #sub>읽기 전용입니다. 금액·상품 ID 변경은 백엔드/운영이 DB 에서 처리합니다.</template>

      <!--
        상품 종류가 주 구분이고 스토어는 그 안의 보조 필터입니다.
        둘을 같은 모양의 탭으로 쌓으면 무엇이 상위인지 알 수 없어, 한 줄에 무게를 달리해 배치합니다.
      -->
      <div v-if="pricingKindTabs.length || storeList.length > 1" class="edit-textbook__price-toolbar">
        <!--
          업그레이드 상품(addon)은 도서가 없는 "기능만 파는" 행이라, 판매 상품과 섞이면
          추가금이 도서 가격처럼 보입니다. 두 종류가 다 있을 때만 탭을 그립니다.
        -->
        <div v-if="pricingKindTabs.length" class="edit-textbook__kind-tabs">
          <button
            v-for="tab in pricingKindTabs"
            :key="tab.value"
            type="button"
            class="edit-textbook__kind-tab"
            :class="{ 'is-active': activePricingKind === tab.value }"
            v-tooltip="tab.sub"
            @click="setPricingKind(tab.value)"
          >
            {{ tab.label }}
            <span class="edit-textbook__kind-tab-count">{{ getPricingKindCount(tab.value) }}</span>
          </button>
        </div>

        <div v-if="storeList.length > 1" class="p-tabs edit-textbook__store-seg">
          <button
            v-for="store in storeList"
            :key="store"
            type="button"
            class="p-tabs__tab"
            :class="{ 'is-active': activePricingStore === store }"
            @click="activePricingStore = store"
          >
            {{ getStoreName(store) }}
            <span class="p-tabs__count">{{ getStoreItemCount(store) }}</span>
          </button>
        </div>
      </div>

      <div class="edit-textbook__price-scroll">
        <table class="edit-textbook__price-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>등급</th>
              <th>설명</th>
              <th class="is-right">도서가</th>
              <th class="is-right">추가금</th>
              <th class="is-right">판매가(총액)</th>
              <th class="is-center">기능 기간</th>
              <th>스토어 상품 ID</th>
              <th>화폐</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(option, index) in filteredPricingByStore" :key="index">
              <td><APill kind="info">{{ option.product_type }}</APill></td>
              <td>{{ option.grade || '—' }}</td>
              <td>{{ option.product_description }}</td>
              <td class="is-right edit-textbook__num">{{ formatPriceCell(option.book_price) }}</td>
              <td class="is-right edit-textbook__num">{{ formatPriceCell(option.addon_amount) }}</td>
              <td class="is-right edit-textbook__num">
                <strong>{{ formatPrice(option.list_price) }}</strong>
                <!-- 묶음 상품이 "빈 행"으로 오해되지 않도록 총액의 구성을 함께 보여 줍니다 -->
                <small v-if="showPriceBreakdown(option)" class="edit-textbook__price-parts">
                  {{ formatPrice(option.book_price) }} + {{ formatPrice(option.addon_amount) }}
                </small>
              </td>
              <td class="is-center">{{ option.feature_days ? `${option.feature_days}일` : '—' }}</td>
              <td class="edit-textbook__num">{{ option.store_product_id || '—' }}</td>
              <td>{{ option.currency_code }}</td>
            </tr>
            <tr v-if="filteredPricingByStore.length === 0">
              <td colspan="9" class="edit-textbook__empty-cell">해당 조건의 가격 정보가 없습니다</td>
            </tr>
          </tbody>
        </table>
      </div>
    </AModal>

    <!-- 가격 등급 안내 모달 -->
    <AModal v-model="showPricingGradeInfo" title="가격 등급 안내" size="lg">
      <h6 class="edit-textbook__modal-h">가격 등급 체계 (3단계)</h6>
      <table class="edit-textbook__grade-table">
        <thead>
          <tr>
            <th>등급</th>
            <th>Badge</th>
            <th>포함 기능</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Basic</td>
            <td><APill kind="default">Basic</APill></td>
            <td>디지털 교재 영구 이용권한, 무제한 재생</td>
          </tr>
          <tr>
            <td>Standard</td>
            <td><APill kind="info">Standard</APill></td>
            <td>Basic 전체 + 인터랙티브 콘텐츠, PIP 기능</td>
          </tr>
          <tr>
            <td>Premium</td>
            <td><APill kind="yellow">Premium</APill></td>
            <td>Standard 전체 + AI 자율대화/작문, AI 보조 평가/학습 분석</td>
          </tr>
        </tbody>
      </table>

      <h6 class="edit-textbook__modal-h">기능별 등급 매핑</h6>
      <table class="edit-textbook__grade-table">
        <thead>
          <tr>
            <th>기능</th>
            <th class="is-center">Basic</th>
            <th class="is-center">Standard</th>
            <th class="is-center">Premium</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in pricingMatrix" :key="row.feature">
            <td>{{ row.feature }}</td>
            <td class="is-center" :class="row.basic ? 'is-ok' : 'is-no'">{{ row.basic ? 'O' : 'X' }}</td>
            <td class="is-center" :class="row.standard ? 'is-ok' : 'is-no'">{{ row.standard ? 'O' : 'X' }}</td>
            <td class="is-center" :class="row.premium ? 'is-ok' : 'is-no'">{{ row.premium ? 'O' : 'X' }}</td>
          </tr>
        </tbody>
      </table>
    </AModal>

    <!-- 버전 이력 모달 -->
    <AModal v-model="isVersionHistoryModalVisible" title="버전 이력" size="lg">
      <table class="edit-textbook__price-table">
        <thead>
          <tr>
            <th>버전</th>
            <th>타입</th>
            <th>작업자</th>
            <th>이메일</th>
            <th>작업 일시</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in versionHistoryList" :key="item.idx">
            <td class="edit-textbook__num"><strong>v{{ item.major_version }}.{{ item.minor_version }}</strong></td>
            <td>
              <APill :kind="item.version_type === 'major' ? 'err' : 'info'">{{ item.version_type }}</APill>
            </td>
            <td>{{ item.user_name || '-' }}</td>
            <td>{{ item.user_email || '-' }}</td>
            <td class="edit-textbook__num">{{ formatHistoryDate(item.created_at) }}</td>
          </tr>
          <tr v-if="versionHistoryList.length === 0">
            <td colspan="5" class="edit-textbook__empty-cell">버전 이력이 없습니다</td>
          </tr>
        </tbody>
      </table>
    </AModal>
  </div>
</template>

<script>
/* eslint-disable prettier/prettier */
import QuillEditor from "@/components/QuillEditor.vue";
import MultipleSelect from "@/components/MultipleSelect.vue";
import { Field, Form as VeeForm, ErrorMessage } from "vee-validate";
import * as yup from "yup";
import swal from "sweetalert2";
import TextbookPageNav from "@/components/TextbookPageNav.vue";
import APageHead from "@/components/Pineapple/APageHead.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";
import AModal from "@/components/Pineapple/AModal.vue";
import TextbookSeriesModal from "@/components/TextbookSeriesModal.vue";
import TextbookFacetModal from "@/components/TextbookFacetModal.vue";
import { formatKoreanDateTime, parseUtc } from "@/utils/datetime";
import { apiErrorMessage, apiErrorStatus } from "@/utils/apiError";
// 가격표 정렬용 등급 순위. 결제 화면과 같은 정의를 씁니다.
import { GRADE_ORDER as PRICING_GRADE_ORDER } from "@/utils/paymentDisplay";

export default {
  name: "EditTextbook",
  components: {
    QuillEditor, MultipleSelect, Field, VeeForm, ErrorMessage,
    TextbookPageNav, APageHead, ACard, ABtn, APill, AModal, TextbookSeriesModal, TextbookFacetModal,
  },
  data() {
    return {
      textbooksData: {
        barcode: "",
        title: "",
        publisher: "",
        grade: "",
        description: "",
        cover_image: "",
        theme: "",
        learning_language: "",
        product_type: "",
        major_version: "",
        minor_version: "",
        is_free_copy: false,
        // 시리즈 미지정이 정상 상태이므로 기본값은 빈 문자열(= select "선택 안 함")입니다.
        series_idx: "",
        series_volume: "",
        // 신규 등록 기본값 = 전역 상한(35). 전역 상한 조회 API는 없습니다(가이드 §9.3).
        max_royalty_rate: 35,
        // 인세 방식(가이드 §9.5). 개편 전 데이터에는 값이 없으므로 기존 방식인 rate 가 기본입니다.
        royalty_type: "rate",
        max_royalty_amount: "",
        max_royalty_currency: "",
      },
      royaltyTypeOptions: [
        { label: "인세율 방식 (%)", value: "rate" },
        { label: "1copy당 정액 방식", value: "amount" },
      ],
      // royalty_type 은 "미전송 = 변경 없음"이라, 폼 기본값을 근거 없이 보내면 방식이 덮입니다.
      // 조회 응답이 실제로 값을 내려줬는지(known) / 관리자가 직접 골랐는지(touched)를 나눠 기억합니다.
      royaltyTypeKnown: false,
      royaltyTypeTouched: false,
      seriesOptions: [],
      showSeriesModal: false,
      previewImage: "",
      // 고른(아직 안 올린) 표지 파일의 미리보기. 저장된 표지(previewImage)보다 먼저 보입니다.
      selectedCoverPreview: "",
      // 저장 뒤 표지 업로드가 실패한 사유. 값이 있으면 표지 카드에 [표지 다시 올리기]가 뜹니다.
      coverUploadError: "",
      coverUploading: false,
      editorConfig: {
        htmlView: true,
        minHeight: "300px",
        toolbarOptions: [
          ["bold", "italic"],
          [{ header: [1, 2, 3, false] }],
          [{ list: "ordered" }, { list: "bullet" }],
          ["link","image"],
        ],
      },
      schema: yup.object({
        barcode: yup
          .string().min(6, this.$t('alert.49', { obj: this.$t('label.28'), count: 6 }))
          .required(this.$t('alert.71', { obj: this.$t('label.28') })),
        title: yup
          .string().required(this.$t('alert.71', { obj: this.$t('label.14') })),
        publisher: yup
          .string().required(this.$t('alert.71', { obj: this.$t('label.29') })),
        grade: yup
          .string().required(this.$t('alert.71', { obj: '학습단계' })),
        theme: yup
          .string().required(this.$t('alert.71', { obj: this.$t('label.37') })),
      }),
      themeOptions: [],
      // 학습단계는 4값 고정 척도지만 표시명을 관리자가 바꿀 수 있어 서버에서 동적 조달합니다. 하드코딩 금지.
      gradeOptions: [],
      // 도서분야·대상언어 마스터. idx는 환경·마이그레이션에 따라 달라질 수 있어 역시 동적 조달합니다.
      fieldOptions: [],
      targetLanguageOptions: [],
      // 학습언어(교재가 가르치는 언어) 선택지. 대상언어와 달리 /common/languages 마스터에서 옵니다.
      learningLanguageOptions: [],
      // 조회 실패와 "마스터가 비어 있음"의 구분. 실패했는데 옵션이 없다고 보여주면
      // 관리자가 기존 값을 지우는 쪽으로 움직입니다.
      learningLanguageFailed: false,
      selectedFieldIdxs: [],
      selectedLanguageIdxs: [],
      // 옵션 조회 실패와 "마스터가 정말 비어 있음"을 구분합니다.
      // 실패를 빈 상태 문구로 보여주면 오도이고, 학습단계는 필수라 신규 등록이 막힙니다.
      facetOptionsFailed: false,
      showFacetModal: false,
      activeFacet: "field",
      currencyOptions: [],
      isModalVisible: false,
      activePricingStore: '',
      productTypeOptions: [
        { label: "A type", value: "A" },
        { label: "B type", value: "B" },
        { label: "C type", value: "C" },
        { label: "D type", value: "D" },
        { label: "E type", value: "E" },
        { label: "F type", value: "F" },
        { label: "G type", value: "G" },
      ],
      productPricing: [],
      // 'total'(판매 상품) / 'addon'(업그레이드 상품)
      activePricingKind: "total",
      pricingGradeOptions: [
        { label: "basic", value: "basic" },
        { label: "standard", value: "standard" },
        { label: "premium", value: "premium" },
      ],
      selectedPricingGrades: ['basic'],
      showPricingGradeInfo: false,
      pricingMatrix: [
        { feature: '디지털 교재 이용권한 제공', basic: true, standard: true, premium: true },
        { feature: '구매 도서 무제한 재생', basic: true, standard: true, premium: true },
        { feature: '인터랙티브 콘텐츠 이용', basic: false, standard: true, premium: true },
        { feature: 'PIP 기능 이용', basic: false, standard: true, premium: true },
        { feature: 'AI기반 자율대화 및 작문 서비스', basic: false, standard: false, premium: true },
        { feature: 'AI 보조 평가 및 학습 분석', basic: false, standard: false, premium: true },
      ],
      tagOptions: [
        { label: "TOPIK", value: "TOPIK" },
        { label: "회화", value: "회화" },
        { label: "문법", value: "문법" },
        { label: "어휘", value: "어휘" },
        { label: "취업", value: "취업" },
        { label: "유학", value: "유학" },
        { label: "여행", value: "여행" },
        { label: "비즈니스", value: "비즈니스" },
      ],
      selectedTags: [],
      newTagInput: '',
      fontOptions: [],
      selectedFonts: [],
      currentStatus: 'none',
      isVersionHistoryModalVisible: false,
      versionHistoryList: [],
    };
  },
  computed: {
    userInfo() {
      return this.$store.state.auth.user_info || {};
    },
    // 승인 게이트 대상은 학습단계·도서분야·테마 3개뿐입니다(가이드 §2).
    // 학습언어·대상언어는 기존 데이터 대부분이 비어 있어 서버가 게이트에서 뺐습니다 —
    // 입력 UI 에는 그 둘도 있지만 여기서는 세지 않습니다.
    // 그래야 배지가 "승인이 막히는 사유"와 정확히 같은 뜻이 됩니다.
    missingApprovalFacets() {
      const missing = [];
      if (!this.textbooksData.grade) missing.push("학습단계");
      if (!this.selectedFieldIdxs.length) missing.push("도서분야");
      if (!this.textbooksData.theme) missing.push("테마");
      return missing;
    },
    // 관리자에게는 상태 전이 제약이 없지만(가이드 §3), 화면에는 지금 의미가 있는 전이만 올립니다.
    // 전부 열어두면 이미 승인된 교재를 또 승인하는 것 같은 선택지가 섞여 오조작을 부릅니다.
    statusActions() {
      const actions = {
        request: [
          { value: 'approval', label: '승인' },
          { value: 'rejection', label: '거절' },
        ],
        // 승인 해제는 저자가 할 수 없으므로(이제 approval 전이 자체가 관리자 전용)
        // 이 선택지가 없으면 잘못 승인한 교재를 스토어에서 내릴 방법이 아예 없습니다.
        approval: [{ value: 'none', label: '승인 해제 (스토어에서 내림)' }],
        rejection: [
          { value: 'approval', label: '승인' },
          { value: 'none', label: '반려 해제 (미요청으로)' },
        ],
      };
      return actions[this.currentStatus] || [];
    },
    // dev 에 옵션 목록에 없는 theme('default')을 가진 교재가 있습니다(가이드 §1).
    // 그대로 두면 select 가 빈칸으로 보여 관리자가 "미지정"으로 오해하고 덮어씁니다.
    // 현재 값을 임시 옵션으로 끼워 넣어 무엇이 저장돼 있는지 보이게 합니다.
    themeSelectOptions() {
      const current = this.textbooksData.theme;
      const options = this.themeOptions || [];
      if (current && !options.some((o) => o.value === current)) {
        return [...options, { value: current, label: `${current} (목록에 없는 값)` }];
      }
      return options;
    },
    // 학습언어도 같은 이유로 방어합니다 — 마스터에서 지워진 코드가 저장돼 있어도 보이게 합니다.
    learningLanguageSelectOptions() {
      const current = this.textbooksData.learning_language;
      const options = this.learningLanguageOptions;
      if (current && !options.some((o) => o.code === current)) {
        return [...options, { code: current, label: `${current} (목록에 없는 코드)` }];
      }
      return options;
    },
    statusLabel() {
      const statusMap = {
        'request': '승인요청',
        'approval': '승인완료',
        'rejection': '거절',
        '': '미요청',
      };
      return statusMap[this.currentStatus] || '미요청';
    },
    statusPillKind() {
      const map = {
        'request': 'warn',
        'approval': 'ok',
        'rejection': 'err',
        '': 'default',
      };
      return map[this.currentStatus] || 'default';
    },
    isAmountRoyalty() {
      return this.textbooksData.royalty_type === "amount";
    },
    // 정액 방식이면 최대 인세율 입력이 화면에서 사라져 사용자가 빈 값을 고칠 수 없습니다.
    // 그 상태로 ""를 보내면 422 가 나므로 전역 상한(35)으로 되돌려 §9.3 리셋 함정도 함께 피합니다.
    maxRoyaltyRatePayload() {
      const rate = this.textbooksData.max_royalty_rate;
      return rate === "" || rate === null || rate === undefined ? 35 : rate;
    },
    // 저장 payload용 시리즈 값. select의 "" 는 반드시 null로 바꿔 보냅니다.
    seriesIdxPayload() {
      const v = this.textbooksData.series_idx;
      return v === "" || v == null ? null : Number(v);
    },
    // 시리즈가 없으면 권차도 무조건 null. 시리즈 없는 교재에 권차만 남는 고아 값을 막습니다.
    seriesVolumePayload() {
      if (this.seriesIdxPayload === null) return null;
      const v = this.textbooksData.series_volume;
      return v === "" || v == null ? null : Number(v);
    },
    // 서버 정렬이 product_kind 우선으로 바뀌어 업그레이드 상품이 먼저 옵니다.
    // 화면은 종전과 같은 "타입 → 등급 → 스토어" 순으로 다시 세웁니다.
    // sort 는 원본 배열을 바꾸므로 반드시 복사본에 겁니다.
    sortedPricing() {
      const rank = (g) => (PRICING_GRADE_ORDER[g] !== undefined ? PRICING_GRADE_ORDER[g] : 9);
      return [...this.productPricing].sort(
        (a, b) =>
          String(a.product_type || "").localeCompare(String(b.product_type || "")) ||
          rank(a.grade) - rank(b.grade) ||
          String(a.store || "").localeCompare(String(b.store || ""))
      );
    },
    // 응답에 한 종류만 있으면(개편 배포 전) 탭을 그리지 않아 종전 화면과 같아집니다.
    pricingKindTabs() {
      const kinds = new Set(this.sortedPricing.map((row) => this.pricingKindOf(row)));
      if (kinds.size < 2) return [];
      return [
        { value: "total", label: "판매 상품", sub: "도서(+기능) 신규 구매용" },
        { value: "addon", label: "업그레이드 상품", sub: "도서 보유자가 기능만 추가 구매" },
      ].filter((tab) => kinds.has(tab.value));
    },
    pricingByKind() {
      if (!this.pricingKindTabs.length) return this.sortedPricing;
      return this.sortedPricing.filter((row) => this.pricingKindOf(row) === this.activePricingKind);
    },
    storeList() {
      const stores = [...new Set(this.pricingByKind.map(item => item.store))];
      return stores.sort();
    },
    filteredPricingByStore() {
      if (!this.activePricingStore) return this.pricingByKind;
      return this.pricingByKind.filter(item => item.store === this.activePricingStore);
    },
  },
  async mounted() {
    this.getThemes();
    this.getFontOptions();
    this.getProductPricing();
    this.loadCurrencies();

    // 시리즈 옵션이 먼저 채워져야 아래 교재 조회 결과의 series_idx가 select에 반영됩니다.
    // 옵션이 없는 상태에서 값을 넣으면 select가 빈 값으로 튀고, 그대로 저장하면 시리즈가 해제됩니다.
    // 분류 옵션(단계·분야·언어)도 같은 이유로 함께 기다립니다.
    await Promise.all([this.getSeries(), this.getFilterOptions(), this.getLearningLanguages()]);

    const barcode = this.$route.query.barcode;
    if (barcode != undefined) {
      this.getTextbook(barcode);
    }
  },
  methods: {
    handleTagEnter(e) {
      if (e.isComposing) return;
      this.addCustomTag();
    },
    addCustomTag() {
      const tag = this.newTagInput.trim();
      if (!tag) return;
      if (!this.tagOptions.some(o => o.value === tag)) {
        this.tagOptions.push({ label: tag, value: tag });
      }
      if (!this.selectedTags.includes(tag)) {
        this.selectedTags = [...this.selectedTags, tag];
      }
      this.newTagInput = '';
    },
    onStatusChange(status) {
      if (!status) return;
      this.changeStatus(status);
    },
    async getThemes() {
      try {
        const res = await this.$axios({
          method: "get",
          url: "/common/themes",
          loadingBar: true,
        });
        if (res.status === 200 && Array.isArray(res.data)) {
          this.themeOptions = res.data;
        } else {
          console.error("Unexpected response format:", res.data);
        }
      } catch (error) {
        console.error("Error :", error);
      }
    },
    // 정액 방식의 통화 선택지. 서버는 currency 마스터에 있는 코드만 받으므로(그 외 400)
    // 조회에 실패해도 주요 통화만이라도 남겨 정액 도서 저장이 통째로 막히지 않게 합니다.
    async loadCurrencies() {
      try {
        const res = await this.$axios({
          method: "get",
          url: "/common/currencies",
          loadingBar: false,
        });
        if (res.status === 200 && res.data) {
          this.currencyOptions = Array.isArray(res.data) ? res.data : res.data.currencies || [];
        }
      } catch (error) {
        console.error("화폐 목록 로드 실패:", error);
        this.currencyOptions = [
          { code: "KRW", name: "원", icon: "₩" },
          { code: "USD", name: "달러", icon: "$" },
          { code: "EUR", name: "유로", icon: "€" },
          { code: "JPY", name: "엔", icon: "¥" },
        ];
      }
    },
    async getFontOptions() {
      try {
        const res = await this.$axios({
          method: 'get',
          url: '/common/fileListInSubdir',
          params: { dir_path: 'fonts/' },
          loadingBar: false,
        });
        const data = res.data;
        let files = [];
        if (Array.isArray(data)) {
          files = data.filter(item => item.type !== 'directory');
        } else if (data && data.children) {
          files = data.children.filter(item => item.type !== 'directory');
        }
        this.fontOptions = files.map(f => ({ label: f.name, value: f.name }));
      } catch (error) {
        this.fontOptions = [];
      }
    },
    // 학습단계·도서분야·대상언어 옵션 일괄 조회.
    // sections.enabled 게이트는 사용자 서비스(Part B)용이므로 여기서는 보지 않습니다 —
    // 관리자 화면까지 가리면 첫 매핑을 영영 만들 수 없습니다.
    async getFilterOptions() {
      try {
        const res = await this.$axios({
          method: "get",
          url: "/textbook/filters",
          params: { lang: "ko" },
        });
        if (res.status === 200 && res.data) {
          this.gradeOptions = res.data.grades || [];
          this.fieldOptions = res.data.fields || [];
          this.targetLanguageOptions = res.data.languages || [];
          this.facetOptionsFailed = false;
        }
      } catch (error) {
        console.error("분류 옵션 조회 실패:", error);
        this.facetOptionsFailed = true;
      }
    },
    // 학습언어 선택지. /common/languages 는 { code, ko, en, ja, ... } 형태로,
    // 코드 외 키가 곧 호칭이라 언어가 늘어도 화면 코드를 고칠 일이 없습니다.
    // 대상언어(/textbook/filters 의 languages)와는 다른 마스터입니다 — 섞지 마세요.
    async getLearningLanguages() {
      try {
        const res = await this.$axios({
          method: "get",
          url: "/common/languages",
        });
        const list = Array.isArray(res.data) ? res.data : [];
        this.learningLanguageOptions = list
          .filter((l) => l && l.code)
          .map((l) => ({ code: l.code, label: l.ko ? `${l.ko} (${l.code})` : l.code }));
        this.learningLanguageFailed = false;
      } catch (error) {
        console.error("학습언어 목록 조회 실패:", error);
        this.learningLanguageOptions = [];
        this.learningLanguageFailed = true;
      }
    },
    openFacetModal(facet) {
      this.activeFacet = facet;
      this.showFacetModal = true;
    },
    // 분야·언어 마스터 모달에서 추가/수정/삭제한 뒤 호출됩니다. 폼의 다른 값은 건드리지 않습니다.
    onFacetMasterChanged({ facet, list }) {
      // 마스터 조회 응답(idx·code·name)이 옵션과 같은 모양이라 그대로 씁니다.
      if (facet === "field") {
        this.fieldOptions = list;
        // 삭제된(목록에서 사라진) 분야가 체크돼 있었다면 해제 — 그대로 저장하면 400이 됩니다.
        const alive = new Set(list.map((o) => o.idx));
        this.selectedFieldIdxs = this.selectedFieldIdxs.filter((idx) => alive.has(idx));
      } else {
        this.targetLanguageOptions = list;
        const alive = new Set(list.map((o) => o.idx));
        this.selectedLanguageIdxs = this.selectedLanguageIdxs.filter((idx) => alive.has(idx));
      }
    },
    // 시리즈 select 옵션. 교재가 참조 중인 시리즈는 서버가 삭제를 막으므로,
    // 필터 없이 전체를 받아두면 선택값이 옵션에서 빠지는 상황이 생기지 않습니다.
    async getSeries() {
      try {
        const res = await this.$axios({
          method: "get",
          url: "/textbook/series",
        });
        if (res.status === 200 && Array.isArray(res.data)) {
          this.seriesOptions = res.data;
        }
      } catch (error) {
        console.error("시리즈 목록 조회 실패:", error);
      }
    },
    // 시리즈가 바뀌면 권차를 초기화합니다.
    // 해제 시에는 고아 권차를, 다른 시리즈로 바꿀 때는 이전 시리즈 권차의 오입력을 막습니다.
    onSeriesChange(value) {
      this.textbooksData.series_idx = value === "" ? "" : Number(value);
      this.textbooksData.series_volume = "";
    },
    // 모달에서 시리즈를 추가/수정/삭제한 뒤 호출됩니다. 교재 폼의 다른 필드는 건드리지 않습니다.
    onSeriesChanged({ list, createdIdx, deletedIdx }) {
      this.seriesOptions = list;

      // 관리 버튼을 누르는 이유는 대개 "지금 필요한 시리즈가 없어서"이므로,
      // 미선택 상태에서 새로 만들었다면 자동으로 선택해 줍니다.
      if (createdIdx && this.textbooksData.series_idx === "") {
        this.textbooksData.series_idx = createdIdx;
      }

      // 선택 중이던 시리즈가 삭제되면 "선택 안 함"으로 되돌리고 권차도 비웁니다.
      if (deletedIdx && Number(this.textbooksData.series_idx) === Number(deletedIdx)) {
        this.textbooksData.series_idx = "";
        this.textbooksData.series_volume = "";
      }
    },
    async getProductPricing() {
      try {
        const res = await this.$axios({
          method: "get",
          url: "/textbook/productPricing",
          loadingBar: true,
        });
        if (res.status === 200 && Array.isArray(res.data)) {
          this.productPricing = res.data;
        } else {
          console.error("Unexpected response format:", res.data);
        }
      } catch (error) {
        console.error("Error :", error);
      }
    },
    async getTextbook(barcode) {
      try {
        const res = await this.$axios({
          method: "get",
          url: `/textbook/editor/textbooks/${barcode}`,
          loadingBar: true,
        });

        if (res.status === 200) {
          const data = res.data;
          this.textbooksData = {
            barcode      : data.barcode,
            title        : data.title,
            publisher    : data.publisher,
            grade        : data.grade,
            description  : data.description,
            cover_image  : data.cover_image,
            theme        : data.theme,
            learning_language: data.learning_language || "",
            product_type : data.product_type,
            major_version: data.major_version,
            minor_version: data.minor_version,
            is_free_copy : data.is_free_copy || false,
            // 시리즈 미지정이면 LEFT JOIN 결과로 3개 모두 null이 옵니다.
            series_idx   : data.series_idx ?? "",
            series_volume: data.series_volume ?? "",
            max_royalty_rate: data.max_royalty_rate ?? 35,
            // 개편 배포 전 응답에는 세 필드가 없습니다. 그때는 기존 방식(rate)으로 봅니다.
            royalty_type: data.royalty_type === "amount" ? "amount" : "rate",
            max_royalty_amount: data.max_royalty_amount ?? "",
            max_royalty_currency: data.max_royalty_currency ?? "",
          };
          // 응답이 방식을 실제로 알려줬을 때만 저장 시 되돌려 보냅니다(위 폴백은 화면 표시용입니다).
          // 스펙상 nullable 인 것은 요청·응답이 같은 모델을 쓰기 때문이고(null = "이번 요청에서
          // 변경 안 함"), 도서의 방식 자체는 항상 있습니다(DB 가 NOT NULL DEFAULT 'rate').
          // 그래서 null 은 "방식이 없다"가 아니라 "읽지 못했다"이며, 키 존재만 보고 확인됐다고
          // 세면 읽은 적 없는 값을 저장 때 되돌려 보내게 됩니다.
          this.royaltyTypeKnown = data.royalty_type === "rate" || data.royalty_type === "amount";
          this.royaltyTypeTouched = false;
          // 분류 체크박스 초기값 — 응답의 fields[].idx / target_languages[].idx
          this.selectedFieldIdxs = Array.isArray(data.fields) ? data.fields.map((f) => f.idx) : [];
          this.selectedLanguageIdxs = Array.isArray(data.target_languages)
            ? data.target_languages.map((l) => l.idx)
            : [];
          this.currentStatus = data.status || '';
          this.selectedTags = this.$stringToArr(data.tags);
          this.selectedTags.forEach(tag => {
            if (!this.tagOptions.some(o => o.value === tag)) {
              this.tagOptions.push({ label: tag, value: tag });
            }
          });
          this.selectedFonts = this.$stringToArr(data.fonts);
          // 서버는 pricing_grade 를 basic/standard/premium 조합만 허용합니다.
          // 화이트리스트 밖 값은 체크박스에 보이지 않으면서 v-model 배열에는 살아남아
          // 저장할 때 그대로 되돌아가 400 을 맞습니다. 읽는 시점에 걸러 둡니다.
          const allowedGrades = this.pricingGradeOptions.map((o) => o.value);
          this.selectedPricingGrades = this.$stringToArr(data.pricing_grade)
            .map((g) => g.toLowerCase())
            .filter((g) => allowedGrades.includes(g));
          if (!this.selectedPricingGrades.includes('basic')) {
            this.selectedPricingGrades.push('basic');
          }
          this.previewImage = data.cover_image ? `${data.cover_image}` : "";

          this.$refs.formRef.setValues(this.textbooksData);
        }
      } catch (error) {
        console.error("Error :", error);
      }
    },
    // 저장 실패 중 분류(시리즈·분야·언어)와 가격 등급이 원인인 것만 구체적으로 안내하고 복구합니다.
    // 다른 관리자가 그 사이 마스터를 삭제한 동시 편집 상황은 정상 흐름으로 취급합니다.
    // 공통 헬퍼($showConfirmationAndSubmit)는 모든 오류를 같은 문구로 처리하고
    // 다른 화면도 함께 쓰므로 건드리지 않고, 여기서 토스트로 실제 사유를 덧붙입니다.
    async handleClassificationSaveError(error) {
      const status = apiErrorStatus(error);
      const message = apiErrorMessage(error, "");

      // 이미 있는 바코드로 등록하면 409 이고 교재는 저장되지 않습니다(docs/교재_등록_표지업로드.md §2).
      // 공통 헬퍼가 "success" 를 돌려주지 않으므로 표지 업로드도 부르지 않습니다.
      if (status === 409) {
        this.$notifyVue({ type: "error", title: "바코드를 확인해주세요", text: message || "이미 등록된 바코드입니다." });
        return;
      }

      // 가격 등급 검증 400. 공통 헬퍼가 제네릭 문구로 덮어버려 사유가 보이지 않으므로 여기서 노출합니다.
      if (status === 400 && message.includes("pricing_grade")) {
        this.$notifyVue({ type: "error", title: "가격 등급 값을 확인해주세요", text: message });
        return;
      }

      // 최대 인세율 검증 400 — detail이 사용자에게 그대로 보여줄 한국어 문장입니다(가이드 §9.4).
      // 전역 상한(35) 초과, 기존 저자 인세율 합계 밑으로 낮추는 경우 등이 여기로 옵니다.
      if (status === 400 && message.includes("인세율")) {
        this.$notifyVue({ type: "error", title: "최대 인세율 값을 확인해주세요", text: message });
        return;
      }

      // 정액(amount) 방식 검증 400 — 최대 인세금액·통화. 역시 detail 을 그대로 보여줍니다(가이드 §9.5.3).
      // 정액 쪽 문구에는 "인세율"이 섞이지 않아 위 인세율 분기를 가로채지 않습니다.
      if (status === 400 && (message.includes("인세금액") || message.includes("정액") || message.includes("통화 코드"))) {
        this.$notifyVue({ type: "error", title: "인세 정액 설정을 확인해주세요", text: message });
        return;
      }

      if (status === 400 && message.includes("존재하지 않는 시리즈")) {
        this.textbooksData.series_idx = "";
        this.textbooksData.series_volume = "";
        await this.getSeries();
        this.$notifyVue({
          type: "error",
          title: "선택한 시리즈를 찾을 수 없습니다",
          text: "이미 삭제된 시리즈입니다. 목록을 새로고침하고 선택을 해제했습니다.",
        });
        return;
      }

      // "존재하지 않는 도서분야입니다: 999" / "존재하지 않는 대상언어입니다: …"
      if (status === 400 && (message.includes("존재하지 않는 도서분야") || message.includes("존재하지 않는 대상언어"))) {
        await this.getFilterOptions();
        // 최신 옵션에 없는 체크를 걷어내면 재저장 시 같은 400이 반복되지 않습니다.
        const aliveFields = new Set(this.fieldOptions.map((o) => o.idx));
        const aliveLangs = new Set(this.targetLanguageOptions.map((o) => o.idx));
        this.selectedFieldIdxs = this.selectedFieldIdxs.filter((idx) => aliveFields.has(idx));
        this.selectedLanguageIdxs = this.selectedLanguageIdxs.filter((idx) => aliveLangs.has(idx));
        this.$notifyVue({
          type: "error",
          title: "삭제된 분류가 선택되어 있었습니다",
          text: "목록을 새로고침하고 해당 항목의 체크를 해제했습니다. 다시 저장해주세요.",
        });
        return;
      }

      // 권차에 "1권" 같은 문자열이 들어간 경우 등
      const detail = error?.response?.data?.detail;
      const isSeriesField = Array.isArray(detail)
        && detail.some((d) => Array.isArray(d.loc) && d.loc.some((l) => String(l).startsWith("series")));
      if (status === 422 && isSeriesField) {
        this.$notifyVue({ type: "error", title: "입력값을 확인해주세요", text: message });
      }
    },
    // 등록(POST)과 수정(PATCH)이 같은 payload를 씁니다.
    // 2026-08-28 부터 PATCH 는 진짜 부분수정입니다 — body 에 넣은 키만 저장되고, 뺀 키는 변경 없음입니다.
    // 이 화면은 폼이 곧 교재 기본정보 전체라 계속 전체를 보냅니다(그래도 결과가 같습니다).
    // 배열은 규칙이 다릅니다: 키 생략 = 미변경 / [] = 전부 해제 이므로,
    // field_ids/language_ids 는 항상 현재 선택 전체를 실어 화면과 서버를 일치시킵니다.
    // category_idx는 백엔드 모델에서 제거된 필드이므로 보내지 않습니다.
    buildTextbookPayload(values) {
      const payload = {
        barcode: values.barcode?.trim() || values.barcode,
        title: values.title,
        publisher: values.publisher,
        grade: values.grade,
        description: this.textbooksData.description,
        tags: this.$arrToString(this.selectedTags),
        fonts: this.$arrToString(this.selectedFonts),
        pricing_grade: this.$arrToString(this.selectedPricingGrades),
        theme: values.theme,
        // 학습언어는 "미입력"이 정상 상태입니다(dev 49권 중 48권이 비어 있음).
        // 빈 값을 "" 로 보내면 부분수정 PATCH 가 빈 문자열을 그대로 저장하므로 null 로 지웁니다.
        learning_language: this.textbooksData.learning_language || null,
        product_type: values.product_type,
        is_free_copy: this.textbooksData.is_free_copy,
        // 종전에는 이 필드를 빼면 서버가 전역 상한(35)으로 되돌렸습니다(정산 가이드 §9.3의 리셋 함정).
        // 2026-08-28 부분수정 전환으로 그 함정은 해소됐지만, 화면 값을 그대로 보내는 것도
        // 결과가 같아 그대로 둡니다. 정액 방식이면 입력창이 숨겨져 빈 값을 고칠 수 없으므로
        // maxRoyaltyRatePayload 가 전역 상한을 채웁니다.
        max_royalty_rate: this.maxRoyaltyRatePayload,
        series_idx: this.seriesIdxPayload,
        series_volume: this.seriesVolumePayload,
        field_ids: [...this.selectedFieldIdxs],
        language_ids: [...this.selectedLanguageIdxs],
      };

      // royalty_type 도 "미전송 = 변경 없음"입니다. 조회 응답이 이 필드를 안 내려주면(개편 미배포)
      // 폼 기본값 rate 가 그대로 전송돼 정액 도서가 무관한 저장 한 번에 rate 로 되돌아갑니다.
      // 그래서 신규 등록이거나, 조회로 방식을 확인했거나, 관리자가 라디오를 직접 바꿨을 때만 보냅니다.
      if (!this.$route.query.barcode || this.royaltyTypeKnown || this.royaltyTypeTouched) {
        payload.royalty_type = this.textbooksData.royalty_type;
      }

      // 최대 인세금액·통화는 max_royalty_rate 와 규칙이 반대입니다 — "미전송 = 변경 없음"이라
      // 인세율 방식일 때 빼고 보내면 도서에 저장돼 있던 정액 설정이 그대로 보존됩니다(가이드 §9.5.1).
      // 반대로 빈 값을 실어 보내면 상한이 0 으로 덮이거나 400 이 납니다.
      if (this.isAmountRoyalty) {
        payload.max_royalty_amount = Number(this.textbooksData.max_royalty_amount);
        payload.max_royalty_currency = this.textbooksData.max_royalty_currency;
      }

      return payload;
    },
    async onSubmit(values) {
      // 정액 방식인데 금액·통화가 비어 있으면 payload 에서 빠지고, 그 두 필드는 "미전송 = 변경 없음"이라
      // 예전 상한이 조용히 살아남습니다. 저장 전에 막아 값이 반영된 것처럼 보이는 상황을 없앱니다.
      const amountCeiling = this.textbooksData.max_royalty_amount;
      if (this.isAmountRoyalty && (amountCeiling === "" || amountCeiling === null || !this.textbooksData.max_royalty_currency)) {
        this.$notifyVue({
          type: "error",
          title: "최대 인세금액을 확인해주세요",
          text: "1copy당 정액 방식에는 최대 인세금액과 통화를 함께 입력해야 합니다.",
        });
        return;
      }

      // 표지 형식은 저장 전에 봅니다. 등록(POST) 뒤에 형식 오류로 멈추면 [저장]을 다시 눌러야 하고,
      // 그 POST 는 같은 바코드라 409 입니다(docs/교재_등록_표지업로드.md §3).
      const coverFile = this.selectedCoverFile();
      if (coverFile && !this.isJpegFileName(coverFile.name)) {
        this.rejectCoverFile();
        return;
      }

      const requestFn = async () => {
        const data = this.buildTextbookPayload(values);
        try {
          if (!this.$route.query.barcode) {
            return await this.$axios({
              method: "post",
              url: "/textbook/editor/textbooks",
              data,
            });
          }
          return await this.$axios({
            method: "patch",
            url: `/textbook/editor/textbooks/${this.$route.query.barcode}`,
            data,
          });
        } catch (error) {
          await this.handleClassificationSaveError(error);
          throw error;
        }
      };

      // 표지 업로드가 실패하면 이 화면에 남아 업로드만 다시 하므로 폼을 비우지 않습니다(resetForm 을 넘기지 않음).
      const result = await this.$showConfirmationAndSubmit(requestFn, null, { 'title': this.$t('alert.47') });
      if (result !== "success") return;

      if (coverFile) {
        const barcode = values.barcode?.trim() || values.barcode;
        if (!(await this.uploadCover(barcode, coverFile))) {
          // 교재는 이미 저장됐습니다. 신규 등록 화면에 그대로 두면 [저장]이 POST 를 다시 보내 409 이므로
          // 방금 만든 교재의 수정 화면으로 바꿉니다. 쿼리만 바뀌면 mounted 가 다시 돌지 않아 직접 읽습니다.
          if (!this.$route.query.barcode) {
            await this.$router.replace({ query: { ...this.$route.query, barcode } });
            await this.getTextbook(barcode);
          }
          return;
        }
      }
      this.goToTextbookList();
    },
    // 성공하면 true. 실패하면 사유를 표지 카드에 남겨 업로드만 다시 하게 합니다(docs/교재_등록_표지업로드.md §3).
    async uploadCover(barcode, file) {
      const formData = new FormData();
      formData.append("cover_image", file);
      this.coverUploading = true;
      try {
        await this.$axios({
          method: "post",
          url: `/textbook/editor/textbooks/${barcode}/upload-cover`,
          data: formData,
        });
        this.coverUploadError = "";
        return true;
      } catch (error) {
        console.error("Error uploading image:", error);
        const status = apiErrorStatus(error);
        this.coverUploadError = apiErrorMessage(
          error,
          status ? `업로드 요청이 실패했습니다 (HTTP ${status}).` : "서버에 연결하지 못했습니다.",
        );
        this.$notifyVue({
          title: this.$t('txt.12'),
          text: this.$t('txt.13'),
          type: "error",
        });
        return false;
      } finally {
        this.coverUploading = false;
      }
    },
    async retryCoverUpload() {
      const file = this.selectedCoverFile();
      if (!file) {
        this.$notifyVue({
          title: "표지 파일을 골라주세요",
          text: "위 표지를 눌러 JPG, JPEG 파일을 고른 뒤 다시 눌러주세요.",
          type: "error",
        });
        return;
      }
      if (await this.uploadCover(this.$route.query.barcode, file)) {
        this.$notifyVue({ title: "표지를 올렸습니다.", type: "success" });
        this.goToTextbookList();
      }
    },
    goToTextbookList() {
      const pageQuery = this.$route.query.page ? { page: this.$route.query.page } : {};
      this.$router.push({ path: "textbooks", query: pageQuery });
    },
    selectedCoverFile() {
      return document.getElementById("upload-image")?.files?.[0] || null;
    },
    // 서버는 원래 파일 이름이 .jpg·.jpeg 인 표지만 받고(그 밖은 400) 대소문자는 가리지 않습니다
    // (docs/교재_등록_표지업로드.md §2). accept 는 파일 창에서 "모든 파일"로 풀 수 있어 여기서도 봅니다.
    isJpegFileName(name) {
      return /\.jpe?g$/i.test(name || "");
    },
    // 고른 파일을 버리고 미리보기를 저장된 표지로 되돌립니다.
    clearCoverSelection() {
      const input = document.getElementById("upload-image");
      if (input) input.value = "";
      this.selectedCoverPreview = "";
    },
    rejectCoverFile() {
      this.clearCoverSelection();
      this.$notifyVue({
        title: "표지 파일 형식을 확인해주세요",
        text: "JPG, JPEG 파일만 업로드 가능합니다.",
        type: "error",
      });
    },
    async getFileName(files) {
      const file = files && files[0];
      // 파일 창을 취소해 선택이 비었으면 미리보기도 저장된 표지로 되돌립니다.
      if (!file) {
        this.clearCoverSelection();
        return;
      }
      if (!this.isJpegFileName(file.name)) {
        this.rejectCoverFile();
        return;
      }
      this.fileName = file;
      await this.base64(this.fileName);
    },
    base64(file) {
      return new Promise(resolve => {
        let a = new FileReader();
        a.onload = e => {
          resolve(e.target.result);
          // 이 값이 있으면 저장된 표지 대신 보입니다. 저장 뒤 교재를 다시 읽어도(getTextbook) 덮이지 않습니다.
          this.selectedCoverPreview = e.target.result;
        };
        a.readAsDataURL(file);
      });
    },
    openModal() {
      this.isModalVisible = true;
      if (this.pricingKindTabs.length && !this.pricingKindTabs.some((t) => t.value === this.activePricingKind)) {
        this.activePricingKind = this.pricingKindTabs[0].value;
      }
      if (this.storeList.length > 0 && !this.storeList.includes(this.activePricingStore)) {
        this.activePricingStore = this.storeList[0];
      }
    },
    closeModal() {
      this.isModalVisible = false;
    },
    getStoreName(store) {
      const storeNames = {
        'apple': 'Apple Store',
        'google': 'Google Play',
      };
      return storeNames[store?.toLowerCase()] || store;
    },
    getStoreItemCount(store) {
      // 활성 종류 안에서 세야 탭 숫자의 합이 표의 행 수와 맞습니다.
      return this.pricingByKind.filter(item => item.store === store).length;
    },
    // 개편 배포 전 응답에는 product_kind 가 없습니다. 전 행을 판매 상품으로 봅니다.
    pricingKindOf(row) {
      return row?.product_kind === "addon" ? "addon" : "total";
    },
    getPricingKindCount(kind) {
      return this.sortedPricing.filter((row) => this.pricingKindOf(row) === kind).length;
    },
    setPricingKind(kind) {
      this.activePricingKind = kind;
      // 종류를 바꾸면 스토어 구성이 달라질 수 있어 유효하지 않은 스토어 탭을 되돌립니다.
      if (!this.storeList.includes(this.activePricingStore)) {
        this.activePricingStore = this.storeList[0] || '';
      }
    },
    // 총액에 추가금이 섞인 묶음 행에서만 구성을 병기합니다.
    showPriceBreakdown(row) {
      return this.pricingKindOf(row) === 'total'
        && row.book_price !== null && row.book_price !== undefined
        && Number(row.addon_amount) > 0;
    },
    formatPrice(price) {
      if (!price) return '0';
      return Number(price).toLocaleString();
    },
    // formatPrice(0) 은 '0' 이라 그대로 쓰면 "값 없음"과 0 원이 구분되지 않습니다.
    formatPriceCell(price) {
      if (price === null || price === undefined) return '—';
      return this.formatPrice(price);
    },
    async changeStatus(status) {
      const barcode = this.$route.query.barcode;
      if (!barcode) return;

      // 버전 하한은 "앞으로 내보내는" 전이에만 겁니다.
      // 되돌리는 전이(none)까지 막으면 버전이 어긋난 교재를 내릴 방법이 사라집니다.
      if (status !== 'none' && this.textbooksData.major_version < 1) {
        this.$notifyVue({
          title: '오류',
          text: 'Major 버전이 1 이상이어야 합니다.',
          type: 'error',
        });
        return;
      }
      // 'none' 은 어디서 왔는지에 따라 뜻이 다릅니다 — 승인 해제(스토어에서 내림) vs 반려 해제.
      const undoMessage = this.currentStatus === 'approval'
        ? '승인을 해제하시겠습니까?'
        : '반려를 해제하시겠습니까?';
      const statusMessages = {
        'request': '승인요청 하시겠습니까?',
        'approval': '승인 처리하시겠습니까?',
        'rejection': '거절 처리하시겠습니까?',
        'none': undoMessage,
      };

      // 승인은 서버가 분류값(학습단계·도서분야·테마)을 검사해 비면 400 입니다(가이드 §2).
      // 화면에서 이미 비어 있는 게 보이면 굳이 왕복하지 않고 여기서 막습니다 —
      // 단, 판단 근거는 "저장된 값"이 아니라 화면 값이므로 서버 검사를 대신하지는 않습니다.
      if (status === 'approval' && this.missingApprovalFacets.length) {
        this.$notifyVue({
          title: '분류값을 먼저 지정해주세요',
          text: `${this.missingApprovalFacets.join(', ')} 이(가) 비어 있어 승인할 수 없습니다. 교재 정보에서 지정한 뒤 저장해주세요.`,
          type: 'error',
        });
        return;
      }

      // 공통 헬퍼($showConfirmationAndSubmit)는 모든 오류를 같은 문구로 덮어 detail 이 사라집니다.
      // 승인 400 의 detail 은 빠진 항목 이름이 그대로 담긴 완성된 한국어 문장이라 그게 곧 안내문입니다.
      const confirmation = await swal.fire({
        title: statusMessages[status],
        // 반려는 현재 API 가 사유를 받지 않습니다. 저자에게 남는 기록이 없다는 걸
        // 누르기 전에 알려야 별도 연락 없이 반려만 하고 끝나는 일이 줄어듭니다.
        // 승인 해제는 이미 배포된 교재를 내리는 동작이라 결과를 분명히 적습니다.
        text: this.statusChangeWarning(status),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t('btn.22'),
        cancelButtonText: this.$t('btn.23'),
      });
      if (!confirmation.isConfirmed) return;

      try {
        await this.$axios({
          method: "patch",
          url: `/textbook/editor/postStatus/${barcode}/${status}`,
          loadingBar: true,
        });
        this.currentStatus = status;
        this.$notifyVue({
          title: '성공',
          text: '상태가 변경되었습니다.',
          type: 'success',
        });
      } catch (error) {
        console.error("상태 변경 실패:", error);
        this.handleStatusChangeError(error, status);
      }
    },
    // 확인창 본문. "되돌리기 어렵거나 결과가 화면 밖에서 일어나는" 전이만 따로 적습니다.
    statusChangeWarning(status) {
      if (status === 'rejection') {
        return '거절 사유는 저장되지 않습니다. 사유는 저자에게 별도로 전달해주세요.';
      }
      if (status === 'none' && this.currentStatus === 'approval') {
        return '앱 스토어에서 내려가 사용자에게 더 이상 노출되지 않습니다. 다시 올리려면 승인을 한 번 더 해야 합니다.';
      }
      if (status === 'none') {
        return '미요청 상태로 되돌립니다. 저자가 다시 승인요청할 수 있게 됩니다.';
      }
      return this.$t('alert.60');
    },
    // 승인·반려 실패 안내. detail 을 그대로 보여주되, 화면만 보고는 원인을 짚기 어려운
    // 두 경우에만 문장을 덧붙입니다.
    handleStatusChangeError(error, status) {
      const httpStatus = apiErrorStatus(error);
      const message = apiErrorMessage(error, '상태 변경에 실패했습니다.');

      // 승인·반려는 2026-08-28 부터 관리자 전용입니다(가이드 §3).
      // 에디터 계정으로 들어와 있으면 여기로 떨어지는데, 403 원문만으로는 이유가 보이지 않습니다.
      if (httpStatus === 403) {
        this.$notifyVue({
          title: '권한이 없습니다',
          text: '승인·반려는 관리자 계정에서만 가능합니다.',
          type: 'error',
        });
        return;
      }

      if (httpStatus === 400 && status === 'approval') {
        // 화면에는 값이 다 있는데 서버가 비었다고 하면 원인은 대개 "아직 저장하지 않음"입니다.
        // 이 안내가 없으면 방금 고른 값을 눈앞에 두고 400 문구와 씨름하게 됩니다.
        const unsaved = this.missingApprovalFacets.length === 0;
        this.$notifyVue({
          title: '승인할 수 없습니다',
          text: unsaved
            ? `${message} (화면에 입력한 분류값이 아직 저장되지 않았을 수 있습니다 — [저장]을 먼저 눌러주세요)`
            : message,
          type: 'error',
        });
        return;
      }

      this.$notifyVue({ title: '오류', text: message, type: 'error' });
    },
    async minorVersionUp() {
      const barcode = this.$route.query.barcode;
      if (!barcode) return;

      const requestFn = async () => {
        return await this.$axios({
          method: "post",
          url: `/textbook/editor/postStatus/${barcode}/minorVersionUp`,
          data: {
            user_idx: this.userInfo.idx,
            user_name: this.userInfo.name,
            user_email: this.userInfo.email,
          },
          loadingBar: true,
        });
      };

      const result = await this.$showConfirmationAndSubmit(requestFn, null, {
        title: 'Minor 버전을 업데이트 하시겠습니까?',
        text: '검토/테스트용 버전이 생성됩니다.',
      });

      if (result === "success") {
        await this.getTextbook(barcode);
        this.$notifyVue({
          title: '성공',
          text: 'Minor 버전이 업데이트되었습니다.',
          type: 'success',
        });
      }
    },
    async majorVersionUp() {
      const barcode = this.$route.query.barcode;
      if (!barcode) return;

      const requestFn = async () => {
        return await this.$axios({
          method: "post",
          url: `/textbook/editor/postStatus/${barcode}/majorVersionUp`,
          data: {
            user_idx: this.userInfo.idx,
            user_name: this.userInfo.name,
            user_email: this.userInfo.email,
          },
          loadingBar: true,
        });
      };

      const result = await this.$showConfirmationAndSubmit(requestFn, null, {
        title: 'Major 버전을 업데이트 하시겠습니까?',
        text: '검토/테스트용 버전이 생성됩니다.',
      });

      if (result === "success") {
        await this.getTextbook(barcode);
        this.$notifyVue({
          title: '성공',
          text: 'Major 버전이 업데이트되었습니다.',
          type: 'success',
        });
      }
    },
    async openVersionHistoryModal() {
      await this.getVersionHistory();
      this.isVersionHistoryModalVisible = true;
    },
    async getVersionHistory() {
      const barcode = this.$route.query.barcode;
      if (!barcode) return;
      try {
        const res = await this.$axios({
          method: "get",
          url: `/textbook/editor/textbooks/${barcode}/versionHistory`,
          loadingBar: true,
        });
        if (res.status === 200 && Array.isArray(res.data)) {
          this.versionHistoryList = res.data;
        }
      } catch (error) {
        console.error("Error :", error);
      }
    },
    formatHistoryDate(value) {
      return formatKoreanDateTime(parseUtc(value));
    },
    async deleteTextbook() {
      const barcode = this.$route.query.barcode;
      if (!barcode) return;

      const requestFn = async () => {
        return await this.$axios({
          method: "delete",
          url: `/textbook/editor/textbooks/${barcode}`,
          loadingBar: true,
        });
      };

      const result = await this.$showConfirmationAndSubmit(requestFn, null, {
        title: '삭제 하시겠습니까?',
        text: '삭제 하시겠습니까?',
      });

      if (result === "success") {
        this.$notifyVue({
          title: '성공',
          text: '삭제되었습니다.',
          type: 'success',
        });
        const pageQuery = this.$route.query.page ? { page: this.$route.query.page } : {};
        this.$router.push({ path: "textbooks", query: pageQuery });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.edit-textbook {
  display: flex;
  flex-direction: column;
  gap: 18px;

  &__version {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: $p-font-mono;
    font-size: 11px;
    font-weight: 800;
    color: $p-point-deep;
    padding: 4px 10px;
    background: $p-point-soft;
    border-radius: $p-radius;

    > i { font-size: 14px !important; }
  }

  &__history-btn {
    width: 32px;
    height: 32px;
    border-radius: $p-radius;
    border: 1px solid $p-line;
    background: $p-panel;
    color: $p-ink-mid;
    display: inline-grid;
    place-items: center;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease;

    > i { font-size: 16px; }

    &:hover {
      background: $p-point-soft;
      color: $p-point-deep;
    }
  }

  &__status-select {
    width: 160px;
    height: 32px;
    font-size: 12px;
  }

  &__layout {
    display: grid;
    grid-template-columns: 280px 1fr;
    gap: 18px;
    align-items: start;

    @media (max-width: 992px) {
      grid-template-columns: 1fr;
    }
  }

  &__cover {
    position: sticky;
    top: 16px;
  }

  &__cover-frame {
    position: relative;
    display: block;
    width: 100%;
    aspect-ratio: 3 / 4;
    border-radius: $p-radius-lg;
    overflow: hidden;
    background: $p-panel-alt;
    border: 1px solid $p-line;
    cursor: pointer;

    &:hover .edit-textbook__cover-overlay {
      opacity: 1;
    }
  }

  &__cover-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &__cover-overlay {
    position: absolute;
    inset: 0;
    background: rgba(26, 22, 18, 0.55);
    display: grid;
    place-items: center;
    gap: 6px;
    color: $p-point;
    font-size: 12px;
    font-weight: 800;
    opacity: 0;
    transition: opacity $p-dur $p-ease;

    > i {
      font-size: 28px !important;
    }
  }

  &__cover-hint {
    margin: 12px 0 0;
    font-size: 11px;
    color: $p-ink-soft;
    text-align: center;
  }

  // 표지 칸이 280px 라 facet-error 와 같은 색으로 세로로 쌓습니다.
  &__cover-error {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 12px;
    padding: 10px 12px;
    border: 1px solid $p-err;
    border-radius: $p-radius-sm;
    background: $p-err-soft;
    color: $p-ink;

    p { margin: 0; font-size: 12px; line-height: 1.5; }
  }

  &__cover-error-title {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    font-weight: 800;

    i { color: $p-err; font-size: 16px; flex-shrink: 0; }
  }

  // 서버 문구에 긴 파일명이 섞일 수 있어 넘칠 때만 끊습니다.
  &__cover-error-reason {
    overflow-wrap: anywhere;
  }

  &__cover-error-hint {
    font-size: 11px;
    color: $p-ink-soft;
  }

  &__form-row {
    margin-bottom: 16px;
  }

  &__editor-field {
    margin-top: 4px;
  }

  &__facets {
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin-bottom: 16px;
    padding: 14px;
    border: 1px solid $p-line-soft;
    border-radius: $p-radius-sm;
    background: $p-panel-alt;
  }

  &__facet-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  &__facet-checks {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 16px;
    margin-top: 4px;
  }

  &__facet-empty {
    font-size: 12px;
    color: $p-ink-soft;
  }

  &__facet-error {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border: 1px solid $p-err;
    border-radius: $p-radius-sm;
    background: $p-err-soft;
    font-size: 12px;
    color: $p-ink;

    i { color: $p-err; font-size: 16px; }
    span { flex: 1; }
  }

  // 승인 게이트 미충족 안내. 조회 실패(facet-error)와 달리 "지금 채우면 되는 일"이라 warn 톤입니다.
  &__facet-gate {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border: 1px solid $p-warn;
    border-radius: $p-radius-sm;
    background: $p-warn-soft;
    font-size: 12px;
    color: $p-ink;

    i { color: $p-warn-ink; font-size: 16px; }
    span { flex: 1; }
    b { color: $p-warn-ink; }
  }

  // 학습언어 / 대상언어는 반대 개념이라 나란히 둬야 라벨을 비교해 읽습니다.
  &__lang-pair {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.6fr);
    gap: 16px;
    align-items: start;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  &__series-row {
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
    gap: 16px;
    align-items: end;
    padding-top: 12px;
    border-top: 1px dashed $p-line;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  &__series-control {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 8px;
    align-items: center;
  }

  &__volume-control {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 6px;
    align-items: center;
  }

  &__volume-unit {
    font-size: 12px;
    font-weight: 700;
    color: $p-ink-mid;
  }

  &__series-hint {
    grid-column: 1 / -1;
    display: flex;
    align-items: center;
    gap: 4px;
    margin: 0;
    font-size: 11px;
    color: $p-ink-soft;

    i { font-size: 14px; }
  }

  &__hint {
    font-size: 10px;
    font-weight: 600;
    color: $p-ink-soft;
    margin-left: 4px;
  }

  &__pricing {
    grid-column: 2;

    @media (max-width: 992px) {
      grid-column: 1;
    }
  }

  &__price-row {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 16px;
    align-items: end;
    margin-bottom: 16px;
  }

  &__price-actions {
    display: flex;
    align-items: center;
  }

  &__free-toggle {
    background: $p-info-soft;
    border-left: 3px solid $p-info;
    padding: 10px 14px;
    border-radius: $p-radius-md;
    align-items: flex-start;
    margin-bottom: 16px;
  }

  &__free-label {
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 13px;
  }

  &__royalty-type {
    margin-bottom: 16px;
  }

  &__royalty-type-row {
    display: flex;
    flex-wrap: wrap;
    gap: 18px;
    margin-top: 4px;
  }

  &__royalty-cap {
    margin-bottom: 16px;
  }

  &__royalty-cap-row {
    display: flex;
    align-items: center;
    gap: 6px;

    > .p-input { width: 120px; }
  }

  &__royalty-amount-row {
    display: flex;
    align-items: center;
    gap: 6px;

    > .p-input { width: 160px; }
    // 통화 select 도 .p-input 이라 같은 구체성입니다. 뒤에 둬서 폭만 다시 잡습니다.
    > .edit-textbook__royalty-currency { width: 110px; }
  }

  &__grade-section {
    margin-bottom: 16px;
  }

  &__grade-heading {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 10px;
  }

  &__info-btn {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: 0;
    background: $p-info-soft;
    color: $p-info;
    display: inline-grid;
    place-items: center;
    cursor: pointer;
    transition: background $p-dur $p-ease;

    > i { font-size: 14px; }

    &:hover {
      background: $p-info;
      color: $p-panel;
    }
  }

  &__grade-row {
    display: flex;
    flex-wrap: wrap;
    gap: 18px;
  }

  &__tags {
    margin-top: 4px;
  }

  &__tag-input {
    display: flex;
    gap: 8px;
    margin-top: 10px;

    > .p-input { flex: 1; }
  }

  // ── 모달 내부 테이블 ──
  // 종류 탭(주) + 스토어 세그먼트(보조)를 한 줄에 놓고, 밑줄은 줄 전체가 받습니다.
  &__price-toolbar {
    display: flex;
    align-items: flex-end;
    gap: 16px;
    border-bottom: 2px solid $p-line;
    margin-bottom: 14px;
    flex-wrap: wrap;
  }

  &__kind-tabs {
    display: flex;
    gap: 4px;
    // overflow 를 주면 안 됩니다. overflow-x 를 auto 로 두면 CSS 규칙상 overflow-y 도 auto 가 되고,
    // 탭의 margin-bottom: -2px(밑줄을 경계선에 겹치는 값) 때문에 세로로 넘쳐 스크롤바가 생깁니다.
    // 탭은 2개뿐이라 가로 스크롤도 필요 없습니다.
  }

  // 스토어는 하위 필터라 밑줄 탭보다 가벼운 세그먼트로 둡니다.
  &__store-seg {
    margin-left: auto;
    margin-bottom: 8px;

    // 종류 탭이 없으면(개편 전 응답) 혼자 오른쪽에 떠 있게 되므로 왼쪽으로 되돌립니다.
    &:only-child { margin-left: 0; }
  }

  &__kind-tab {
    border: 0;
    background: transparent;
    color: $p-ink-mid;
    font-family: $p-font-body;
    font-size: 12px;
    font-weight: 700;
    padding: 10px 14px;
    border-bottom: 3px solid transparent;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: color $p-dur $p-ease, border-color $p-dur $p-ease;
    margin-bottom: -2px;

    &:hover {
      color: $p-ink;
    }

    &.is-active {
      color: $p-point-deep;
      border-bottom-color: $p-point-deep;
    }
  }

  // 컬럼이 9개라 좁은 화면에서 표만 가로로 흐르게 합니다.
  &__price-scroll {
    overflow-x: auto;
  }

  &__price-parts {
    display: block;
    margin-top: 2px;
    font-family: $p-font-mono;
    font-size: 10px;
    color: $p-ink-soft;
  }

  &__kind-tab-count {
    background: $p-line-soft;
    color: $p-ink-mid;
    border-radius: 999px;
    padding: 1px 7px;
    font-size: 10px;
    font-weight: 700;
  }

  &__price-table,
  &__grade-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;

    th {
      text-align: left;
      font-size: 10px;
      font-weight: 800;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      color: $p-ink-mid;
      padding: 10px 12px;
      background: $p-panel-alt;
      border-bottom: 1px solid $p-line;
    }

    td {
      padding: 10px 12px;
      border-bottom: 1px solid $p-line-soft;
      color: $p-ink;
      vertical-align: middle;
    }

    tr:hover td {
      background: $p-panel-alt;
    }

    .is-right { text-align: right; }
    .is-center { text-align: center; }
    .is-ok { color: $p-ok; font-weight: 800; }
    .is-no { color: $p-err; font-weight: 800; }
  }

  &__grade-table + &__modal-h {
    margin-top: 20px;
  }

  &__num {
    font-family: $p-font-mono;
    font-variant-numeric: tabular-nums;
  }

  &__modal-h {
    margin: 0 0 12px;
    font-size: 13px;
    font-weight: 800;
    color: $p-ink;
  }

  &__empty-cell {
    text-align: center;
    color: $p-ink-soft;
    padding: 28px 12px !important;
    font-size: 12px;
  }
}
</style>
